import React, { memo, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Grid } from '@mui/material';
import {
  Focus,
  GetWeeklyForecastRequestFilters,
} from '@provider-portal/common/api/gen/CampaignDeliveryProvider';
import { PromotionTile } from '@provider-portal/promotions-common/components/PromotionTile/PromotionTile';

import { useConstant } from 'root/hooks/useConstant';

export interface PromotionsFocusGridProps {
  focuses?: Focus[];
  onClickNewPromotion: (filters: GetWeeklyForecastRequestFilters, key: string) => void;
}

export enum FocusKeys {
  GROW_SALES = 'grow_sales',
  ATTRACT_CLIENTS = 'attract_clients',
  RETAIN_CLIENTS = 'retain_clients',
  GET_CHURNED_USERS = 'get_churned_users',
}

function getFocusItems(focuses: Focus[], dataMap: Record<FocusKeys, { title: string }>) {
  const focusItems = [];
  for (const focus of focuses) {
    focusItems.push({
      title: dataMap[focus.key].title,
      key: focus.key,
      weeklySpendValue: `${focus.weekly_spend_value.value}`,
      weeklyImpactValue: `${focus.weekly_impact_value.value}`,
      newUsers: '',
      currency: `${focus.weekly_spend_value.currency}`,
      filters: focus.filters,
    });
  }
  return focusItems;
}

const useStrings = () => {
  const intl = useIntl();

  return useMemo(
    () => ({
      growSales: intl.formatMessage({ id: 'components.SetupNewPromotion.growSales' }),
      attractClients: intl.formatMessage({ id: 'components.SetupNewPromotion.attractClients' }),
      retainClients: intl.formatMessage({ id: 'components.SetupNewPromotion.retainClients' }),
      getChurnedUsers: intl.formatMessage({ id: 'components.SetupNewPromotion.getChurnedUsers' }),
    }),
    [intl],
  );
};

export const PromotionsFocusGrid = memo(
  ({ onClickNewPromotion, focuses }: PromotionsFocusGridProps) => {
    const strings = useStrings();
    const focusKeys: Record<FocusKeys, { title: string }> = {
      [FocusKeys.GROW_SALES]: { title: strings.growSales },
      [FocusKeys.ATTRACT_CLIENTS]: { title: strings.attractClients },
      [FocusKeys.GET_CHURNED_USERS]: { title: strings.getChurnedUsers },
      [FocusKeys.RETAIN_CLIENTS]: { title: strings.retainClients },
    };

    const { data: FOCUS_KEYS_TO_INFORMATION_MAPPING } = useConstant(() => ({
      data: focusKeys,
    }));

    const onFocusClick = useCallback(
      (focus, key) => {
        return () => {
          onClickNewPromotion(focus, key);
        };
      },
      [onClickNewPromotion],
    );

    const focusItems = getFocusItems(focuses, FOCUS_KEYS_TO_INFORMATION_MAPPING);

    return (
      <Grid container rowSpacing="24px" columnSpacing={3}>
        {focusItems.map((obj) => {
          return (
            <Grid item xs={6} key={obj.key}>
              <PromotionTile
                title={obj.title}
                weeklySpendValue={obj.weeklySpendValue}
                onClickNewPromotion={onFocusClick(obj.filters, obj.key)}
                weeklyImpactValue={obj.weeklyImpactValue}
                currency={obj.currency}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  },
);
